import { createContext, useContext, ReactNode, useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DateRange } from "react-day-picker";
import { logger } from "@/utils/logger";

const DATE_RANGE_STORAGE_KEY = "dashboard-date-range";

interface DashboardDataContextType {
  pages: any[] | undefined;
  pagesLoading: boolean;
  pagesError: Error | null;
  dashboardData: any;
  dashboardLoading: boolean;
  dashboardError: Error | null;
  dateRange: DateRange | undefined;
  setDateRange: (range: DateRange | undefined) => void;
  refetchPages: () => void;
  refetchDashboard: () => void;
  clearDateRange: () => void;
  isReady: boolean;
}

const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined);

export const DashboardDataProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  
  // Initialize date range from localStorage with stale check
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const saved = localStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const from = parsed.from ? new Date(parsed.from) : undefined;
        const to = parsed.to ? new Date(parsed.to) : undefined;
        
        // Clear stale date range if end date is before today
        // This prevents old saved filters from hiding new data
        if (to) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const toDate = new Date(to);
          toDate.setHours(0, 0, 0, 0);
          
          if (toDate < today) {
            logger.info("Clearing stale date range filter", { savedTo: to, today });
            localStorage.removeItem(DATE_RANGE_STORAGE_KEY);
            return undefined;
          }
        }
        
        return { from, to };
      } catch (e) {
        logger.warn("Failed to parse saved date range", e);
        return undefined;
      }
    }
    return undefined;
  });

  // Persist date range to localStorage
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      localStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify({
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
      }));
    } else {
      localStorage.removeItem(DATE_RANGE_STORAGE_KEY);
    }
  }, [dateRange]);

  // Clear date range method
  const clearDateRange = useCallback(() => {
    setDateRange(undefined);
    localStorage.removeItem(DATE_RANGE_STORAGE_KEY);
  }, []);

  // Shared feedback pages query
  const {
    data: pages,
    isLoading: pagesLoading,
    error: pagesError,
    refetch: refetchPages,
  } = useQuery({
    queryKey: ["feedback-pages", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      try {
        const { data, error } = await supabase
          .from("feedback_pages")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) {
          logger.error("Failed to fetch feedback pages", error);
          throw error;
        }
        return data || [];
      } catch (error) {
        logger.error("Error in feedback pages query", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 60000,
    gcTime: 300000,
    refetchOnMount: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Shared dashboard data query with date filtering
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ["dashboard-data", user?.id, dateRange?.from, dateRange?.to],
    queryFn: async () => {
      if (!user) return null;

      try {
        logger.debug("Fetching dashboard data", {
          userId: user.id,
          dateRange: dateRange ? { from: dateRange.from?.toISOString(), to: dateRange.to?.toISOString() } : 'none'
        });
        
        const { data, error } = await supabase.rpc("get_dashboard_data", {
          user_uuid: user.id,
          start_date: dateRange?.from?.toISOString() || null,
          end_date: dateRange?.to?.toISOString() || null,
        });

        if (error) {
          logger.error("Failed to fetch dashboard data", error, {
            userId: user.id,
            dateRange: { from: dateRange?.from, to: dateRange?.to }
          });
          throw error;
        }
        
        // Debug log the received data
        const rawData = data as Record<string, unknown>;
        logger.debug("Dashboard data received", {
          hasData: !!data,
          keys: rawData ? Object.keys(rawData) : [],
          stats: rawData?.stats,
          sentimentTotal: (rawData?.sentiment_data as Record<string, unknown>)?.total,
          trendingCount: Array.isArray(rawData?.trending_topics) ? rawData.trending_topics.length : 0
        });
        
        // Validate and ensure data structure
        if (!data) {
          return {
            stats: {
              totalPages: 0,
              activePages: 0,
              inactivePages: 0,
              totalSubmissions: 0,
              submissions24h: 0,
              submissions7d: 0,
              avgSubmissionsPerPage: 0
            },
            sentimentData: null,
            trendingTopics: [],
            recentActivity: []
          };
        }
        
        // Handle both camelCase and snake_case keys from RPC
        const transformedData = {
          stats: rawData.stats || {
            totalPages: 0,
            activePages: 0,
            inactivePages: 0,
            totalSubmissions: 0,
            submissions24h: 0,
            submissions7d: 0,
            avgSubmissionsPerPage: 0
          },
          sentimentData: rawData.sentimentData || rawData.sentiment_data || null,
          trendingTopics: rawData.trendingTopics || rawData.trending_topics || [],
          recentActivity: rawData.recentActivity || rawData.recent_activity || []
        };
        
        logger.debug("Transformed dashboard data", {
          sentimentTotal: (transformedData.sentimentData as Record<string, unknown>)?.total,
          trendingCount: Array.isArray(transformedData.trendingTopics) ? transformedData.trendingTopics.length : 0
        });
        
        return transformedData;
      } catch (error) {
        logger.error("Error in dashboard data query", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 60000,
    gcTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  const contextValue = useMemo(
    () => ({
      pages,
      pagesLoading,
      pagesError: pagesError as Error | null,
      dashboardData,
      dashboardLoading,
      dashboardError: dashboardError as Error | null,
      dateRange,
      setDateRange,
      clearDateRange,
      refetchPages,
      refetchDashboard,
      isReady: !!user && !pagesLoading && !dashboardLoading,
    }),
    [
      pages,
      pagesLoading,
      pagesError,
      dashboardData,
      dashboardLoading,
      dashboardError,
      dateRange,
      setDateRange,
      clearDateRange,
      refetchPages,
      refetchDashboard,
      user,
    ]
  );

  return (
    <DashboardDataContext.Provider value={contextValue}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    // Return safe defaults during initialization instead of throwing
    logger.warn("useDashboardData called outside provider, returning defaults");
    return {
      pages: [],
      pagesLoading: true,
      pagesError: null,
      dashboardData: null,
      dashboardLoading: true,
      dashboardError: null,
      dateRange: undefined,
      setDateRange: () => {},
      clearDateRange: () => {},
      refetchPages: () => {},
      refetchDashboard: () => {},
      isReady: false,
    };
  }
  return context;
};
